// The fake api's tax-registration routes (F-PAY-11, spec/06 §10.4), in the contract's shapes — enough
// for the Tax and invoices settings group to be rendered against the fake in `fake-server.ts`:
//
//   GET  …/venues/{v}/tax-registration                     getVenueTaxRegistration        venues:read
//   POST …/venues/{v}/tax-registration                     scheduleVenueTaxRegistration   tax:manage
//   POST …/venues/{v}/tax-registration/{id}/withdraw       withdrawVenueTaxRegistration   tax:manage
//   GET  …/organizations/{o}/tax-categories                listTaxCategories              settings:read
//   POST …/organizations/{o}/tax-categories                createTaxCategory              tax:manage
//   PATCH …/organizations/{o}/tax-categories/{id}          updateTaxCategory              tax:manage
//
// The organisation's tax categories (F-PAY-11) are the India defaults the server seeds on first read.
//
// The venue has been `composition` under GSTIN 29ABCDE1234F1ZW since 1 April 2026; the business day
// is 22 September 2026 (the fake server's clock, in Asia/Kolkata). Bodies are checked with the
// contract's own schema, so a GSTIN with a wrong check character is refused in the server's words;
// each route checks its own permission, as the server's guard does. A change dated before the
// business day answers `409 conflict`, and withdrawing one whose day has begun does too, as
// `tax-registration.ts` in the commerce module does.

import type { Problem, TaxCategory, TaxRegistration, TaxRegistrationView } from '@clavian/api-client';
import { CreateTaxCategorySchema, ScheduleTaxRegistrationSchema, UpdateTaxCategorySchema, type z } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const RECORDED_AT = '2026-03-28T06:30:00.000Z';
const NOW = '2026-09-22T09:00:00.000Z';

export const FAKE_TAX = {
  businessDay: '2026-09-22',
  gstin: '29ABCDE1234F1ZW',
  /** A second valid GSTIN, for a change of registration. */
  otherGstin: '29AAACP1234Q1Z4',
} as const;

const TAX_ROUTE = /^\/v1\/organizations\/([^/]+)\/venues\/([^/]+)\/tax-registration(?:\/([^/]+)\/withdraw)?$/;
const CATEGORY_ROUTE = /^\/v1\/organizations\/([^/]+)\/tax-categories(?:\/([^/]+))?$/;

/** The India defaults the server seeds (`TAX_CATEGORY_DEFAULTS`), as the list answers them. */
function seededCategories(): TaxCategory[] {
  const row = (tail: string, name: string, hsnSac: string | null, rateBps: number, taxPoint: 'sale' | 'redemption' = 'sale'): TaxCategory => ({
    id: `txc_01k5znw4hmfay8vvr14d2pf2${tail}`,
    name,
    hsnSac,
    rateBps,
    cessRateBps: null,
    taxPoint,
    archived: false,
    createdAt: RECORDED_AT,
    updatedAt: RECORDED_AT,
  });
  return [
    row('g1', 'Food and beverages', '996331', 500),
    row('g2', 'Gaming time', '999699', 1800),
    row('g3', 'Memberships', '999699', 1800),
    row('g4', 'Packaged goods', null, 1800),
    row('g5', 'Wallet top-up', null, 0, 'redemption'),
  ];
}

export interface TaxHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly gate: () => FakeResponse | undefined;
  readonly can: (permission: string) => boolean;
  /** Whether the organisation's legal name and registered address are recorded (ADR-0046); recorded unless a test says not. */
  readonly businessDetailsComplete?: () => boolean;
}

/** One recorded change, before its status is worked out for the day asked about. */
interface Row {
  readonly id: string;
  readonly mode: TaxRegistration['mode'];
  readonly gstin: string | null;
  readonly effectiveOn: string;
  readonly reason: string | null;
  readonly recordedAt: string;
  withdrawnAt: string | null;
}

/** What one write asked for, for tests to read back. */
export interface TaxCall {
  readonly route: 'schedule' | 'withdraw';
  readonly body: unknown;
  readonly registrationId?: string;
}

const IDS = ['vtr_01k5znw4hmfay8vvr14d2pf2e1', 'vtr_01k5znw4hmfay8vvr14d2pf2e2', 'vtr_01k5znw4hmfay8vvr14d2pf2e3', 'vtr_01k5znw4hmfay8vvr14d2pf2e4'];

export function createFakeTax(helpers: TaxHelpers) {
  const { json, problem } = helpers;
  const state = {
    businessDay: FAKE_TAX.businessDay as string,
    rows: [
      { id: IDS[0]!, mode: 'composition', gstin: FAKE_TAX.gstin, effectiveOn: '2026-04-01', reason: 'Joined the composition scheme', recordedAt: RECORDED_AT, withdrawnAt: null },
    ] as Row[],
    calls: [] as TaxCall[],
    categories: seededCategories(),
  };

  /** The latest not-withdrawn change whose day has begun — the one in force (spec/06 §10.4). */
  const inForce = (): Row | undefined =>
    state.rows
      .filter((row) => row.withdrawnAt === null && row.effectiveOn <= state.businessDay)
      .sort((a, b) => (a.effectiveOn === b.effectiveOn ? b.recordedAt.localeCompare(a.recordedAt) : b.effectiveOn.localeCompare(a.effectiveOn)))[0];

  const toDto = (row: Row, inForceId: string | undefined): TaxRegistration => ({
    id: row.id,
    mode: row.mode,
    gstin: row.gstin,
    effectiveOn: row.effectiveOn,
    reason: row.reason,
    status:
      row.withdrawnAt !== null ? 'withdrawn' : row.id === inForceId ? 'in_force' : row.effectiveOn > state.businessDay ? 'scheduled' : 'superseded',
    recordedAt: row.recordedAt,
    withdrawnAt: row.withdrawnAt,
  });

  function view(): TaxRegistrationView {
    const current = inForce();
    const dtos = state.rows.map((row) => toDto(row, current?.id));
    return {
      venueId: VENUE_ID,
      businessDay: state.businessDay,
      current:
        current === undefined
          ? { mode: 'unregistered', gstin: null, effectiveOn: null, source: 'default' }
          : { mode: current.mode, gstin: current.gstin, effectiveOn: current.effectiveOn, source: 'registration' },
      scheduled: dtos.filter((dto) => dto.status === 'scheduled').sort((a, b) => a.effectiveOn.localeCompare(b.effectiveOn)),
      history: [...dtos].reverse(),
    };
  }

  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });

  function handleCategories(method: string, taxCategoryId: string | undefined, input: Record<string, unknown>): FakeResponse | undefined {
    const forbidden = (permission: string) => (helpers.can(permission) ? undefined : problem(403, 'forbidden', 'Not allowed'));
    if (taxCategoryId === undefined && method === 'GET') return forbidden('settings:read') ?? json(200, { categories: state.categories });
    if (taxCategoryId === undefined && method === 'POST') {
      const denied = forbidden('tax:manage');
      if (denied !== undefined) return denied;
      const parsed = CreateTaxCategorySchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      if (state.categories.some((category) => category.name === parsed.data.name)) return problem(409, 'conflict', 'A tax category with that name already exists.');
      const created: TaxCategory = {
        id: `txc_01k5znw4hmfay8vvr14d2pf2h${state.categories.length}`,
        name: parsed.data.name,
        hsnSac: parsed.data.hsnSac ?? null,
        rateBps: parsed.data.rateBps,
        cessRateBps: parsed.data.cessRateBps ?? null,
        taxPoint: parsed.data.taxPoint ?? 'sale',
        archived: false,
        createdAt: NOW,
        updatedAt: NOW,
      };
      state.categories.push(created);
      return json(201, created);
    }
    if (taxCategoryId !== undefined && method === 'PATCH') {
      const denied = forbidden('tax:manage');
      if (denied !== undefined) return denied;
      const parsed = UpdateTaxCategorySchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const index = state.categories.findIndex((category) => category.id === taxCategoryId);
      if (index === -1) return problem(404, 'not_found', 'No such tax category in this organisation.');
      const current = state.categories[index]!;
      const next: TaxCategory = {
        ...current,
        ...(parsed.data.name === undefined ? {} : { name: parsed.data.name }),
        ...(parsed.data.hsnSac === undefined ? {} : { hsnSac: parsed.data.hsnSac }),
        ...(parsed.data.rateBps === undefined ? {} : { rateBps: parsed.data.rateBps }),
        ...(parsed.data.archived === undefined ? {} : { archived: parsed.data.archived }),
        updatedAt: NOW,
      };
      state.categories[index] = next;
      return json(200, next);
    }
    return undefined;
  }

  function handle(method: string, pathname: string, _query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    const category = CATEGORY_ROUTE.exec(pathname);
    if (category !== null) {
      if (category[1] !== ORGANIZATION_ID) return undefined;
      const refused = helpers.gate();
      if (refused !== undefined) return refused;
      return handleCategories(method, category[2], input);
    }
    const match = TAX_ROUTE.exec(pathname);
    if (match === null) return undefined;
    const [, organizationId, venueId, registrationId] = match;
    if (organizationId !== ORGANIZATION_ID) return undefined;
    const refused = helpers.gate();
    if (refused !== undefined) return refused;
    if (venueId !== VENUE_ID) return problem(404, 'not_found', 'No such venue in this organisation.');
    const forbidden = (permission: string) => (helpers.can(permission) ? undefined : problem(403, 'forbidden', 'Not allowed'));

    if (registrationId === undefined && method === 'GET') return forbidden('venues:read') ?? json(200, view());

    if (registrationId === undefined && method === 'POST') {
      const denied = forbidden('tax:manage');
      if (denied !== undefined) return denied;
      state.calls.push({ route: 'schedule', body: input });
      const parsed = ScheduleTaxRegistrationSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      if (parsed.data.effectiveOn < state.businessDay) {
        return problem(409, 'conflict', 'The effective day is in the past; a change takes effect today or later.');
      }
      // A GST document is issued under the organisation's legal name and registered address (ADR-0046).
      if (parsed.data.mode !== 'unregistered' && helpers.businessDetailsComplete?.() === false) {
        return problem(409, 'business_details_required', 'Business details needed first');
      }
      state.rows.push({
        id: IDS[state.rows.length] ?? `vtr_01k5znw4hmfay8vvr14d2pf2f${state.rows.length}`,
        mode: parsed.data.mode,
        gstin: parsed.data.gstin ?? null,
        effectiveOn: parsed.data.effectiveOn,
        reason: parsed.data.reason ?? null,
        recordedAt: NOW,
        withdrawnAt: null,
      });
      return json(201, view());
    }

    if (registrationId !== undefined && method === 'POST') {
      const denied = forbidden('tax:manage');
      if (denied !== undefined) return denied;
      state.calls.push({ route: 'withdraw', body: input, registrationId });
      const row = state.rows.find((candidate) => candidate.id === registrationId);
      if (row === undefined) return problem(404, 'not_found', 'No such scheduled change for this venue.');
      if (row.withdrawnAt !== null) return problem(409, 'conflict', 'That change has already been withdrawn.');
      if (row.effectiveOn <= state.businessDay) {
        return problem(409, 'conflict', 'That change’s business day has begun, so it is permanent; undo it with another dated change.');
      }
      row.withdrawnAt = NOW;
      return json(200, view());
    }

    return undefined;
  }

  return { handle, state };
}

export type FakeTax = ReturnType<typeof createFakeTax>;
